import { Instagram, YouTube } from '@mui/icons-material';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import {
    Drawer,
    Typography,
    Box,
    IconButton,
    TextField,
    Button,
    CircularProgress,
    useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useState, useCallback, useMemo, useEffect } from 'react';
import { useSnackbar } from 'notistack';
import { useCreateList } from '../../hooks/usePlanSummary';
import styles from './CreateNewListDrawer.styles';

// Kept in sync with the backend's validateAddList/validateAddInfluencers enums —
// Twitter/Facebook aren't offered here on purpose: there's no profile-resolution
// path for them yet, so a list created for either would be a permanent dead end
// (nothing could ever be added to it).
const PLATFORMS = Object.freeze([
    { key: 'instagram', label: 'Instagram', icon: Instagram },
    { key: 'youtube', label: 'Youtube', icon: YouTube },
]);

const CreateNewListDrawer = ({ open, onClose, planId }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));

    const [name, setName] = useState('');
    const [platform, setPlatform] = useState(null);

    const { mutate, isPending } = useCreateList(planId);
    const { enqueueSnackbar } = useSnackbar();

    const reset = useCallback(() => {
        setName('');
        setPlatform(null);
    }, []);

    useEffect(() => {
        if (!open) {
            reset();
        }
    }, [open, reset]);

    const handleClose = useCallback(() => {
        reset();
        onClose?.();
    }, [reset, onClose]);

    const handleNameChange = useCallback((e) => {
        const value = e.target.value;
        if (value.length <= 60) {
            setName(value);
        }
    }, []);

    const handlePlatformSelect = useCallback(
        (key) => setPlatform((prev) => (prev === key ? null : key)),
        []
    );

    const trimmedName = name.trim();
    const isValid = Boolean(trimmedName) && Boolean(platform);

    const handleCreate = useCallback(() => {
        if (!isValid) return;
        mutate(
            { name: trimmedName, platform },
            {
                onSuccess: () => {
                    enqueueSnackbar('List created', { variant: 'success' });
                    reset();
                    onClose?.();
                },
                onError: (error) => {
                    const message =
                        error?.response?.data?.message ||
                        "Couldn't create the list. Try again.";
                    enqueueSnackbar(message, { variant: 'error' });
                },
            }
        );
    }, [isValid, trimmedName, platform, mutate, reset, onClose, enqueueSnackbar]);

    const handleKeyDown = useCallback((e) => {
        if (e.key === 'Enter' && isValid && !isPending) {
            handleCreate();
        }
        if (e.key === 'Escape') {
            handleClose();
        }
    }, [isValid, isPending, handleCreate, handleClose]);

    const paperSx = useMemo(() => styles.paper(isMobile), [isMobile]);
    const platformColumns = isMobile ? '1fr 1fr' : isTablet ? '1fr 1fr 1fr' : '1fr 1fr 1fr 1fr';

    const selectedPlatformLabel = platform 
        ? PLATFORMS.find(p => p.key === platform)?.label 
        : '';

    return (
        <Drawer
            anchor="right"
            open={open}
            onClose={handleClose}
            keepMounted={false}
            ModalProps={{ 
                keepMounted: false,
                disableScrollLock: true
            }}
            PaperProps={{ 
                sx: paperSx,
                onKeyDown: handleKeyDown
            }}
            transitionDuration={{ 
                enter: 300, 
                exit: 250 
            }}
        >
            <Box sx={styles.header}>
                <Typography 
                    variant="h6" 
                    sx={styles.heading1}
                >
                    Create New List
                </Typography>
                <IconButton 
                    onClick={handleClose} 
                    size={isMobile ? "small" : "medium"} 
                    aria-label="Close"
                    sx={styles.closeIcon}
                >
                    <CloseIcon fontSize={isMobile ? "small" : "medium"} />
                </IconButton>
            </Box>

            <Box sx={styles.body}>
                <Typography sx={styles.sectionLabel}>List Details</Typography>
                <Typography 
                    variant="body2" 
                    color="text.secondary" 
                    sx={styles.bodyHeading1}
                >
                    List Name
                </Typography>
                <TextField
                    fullWidth
                    placeholder="Enter list name"
                    value={name}
                    onChange={handleNameChange}
                    autoFocus={!isMobile}
                    inputProps={{ 
                        maxLength: 60,
                        'aria-label': 'List name'
                    }}
                    helperText={`${name.length}/60 characters`}
                    FormHelperTextProps={{
                        sx: {
                            textAlign: 'right',
                            mr: 0,
                            color: name.length > 50 ? 'warning.main' : 'text.secondary',
                            fontSize: { xs: '0.7rem', sm: '0.75rem' }
                        }
                    }}
                    sx={{ 
                        mb: 3,
                        '& .MuiOutlinedInput-root': {
                            borderRadius: 2,
                            fontSize: { xs: '0.9rem', sm: '1rem' }
                        }
                    }}
                />

                <Typography sx={styles.sectionLabel}>Choose a Platform</Typography>
                <Box sx={{ 
                    ...styles.platformRow,
                    gridTemplateColumns: platformColumns,
                }}>
                    {PLATFORMS.map(({ key, label, icon: Icon }) => (
                        <Button
                            key={key}
                            startIcon={<Icon fontSize={isMobile ? "small" : "medium"} />}
                            onClick={() => handlePlatformSelect(key)}
                            sx={styles.platformButton(platform === key)}
                        >
                            <Box sx={{ 
                                display: 'flex',
                                alignItems: 'center',
                                // background:'red',
                                gap: { xs: 0.5, sm: 1 }
                            }}>
                                {label}
                            </Box>
                        </Button>
                    ))}
                </Box>

                {/* Platform Selection Feedback */}
                {platform ? (
                    <Box
                        sx={styles.platformFeedback}
                    >
                        <CheckCircleIcon
                            sx={styles.platformCheckCircleIcon}
                        />
                        <Typography
                            variant="body2"
                            sx={styles.platformSelectedLabel}
                        >
                            {selectedPlatformLabel} selected
                        </Typography>
                    </Box>
                ) : (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                        A platform is required to create a list.
                    </Typography>
                )}

            </Box>

            <Box sx={styles.footer}>
                <Button
                    fullWidth
                    variant="outlined"
                    onClick={handleClose}
                    disabled={isPending}
                    sx={styles.cancelButton}
                >
                    Cancel
                </Button>
                <Button
                    fullWidth
                    variant="contained"
                    disabled={!isValid || isPending}
                    onClick={handleCreate}
                    sx={styles.createButton}
                >
                    {isPending ? <CircularProgress size={20} color="inherit" /> : 'Create List'}
                </Button>
            </Box>
        </Drawer>
    );
};

export default CreateNewListDrawer;
